// Ручное управление подпиской «Resstro Оптовик» из супер-админки.
// Оптовик платит 2 999 ₽/мес через ЮKassa, но часть клиентов платит наличными
// или по счёту — оператору нужно уметь докинуть дни без платёжки. Две формы:
//   • Триал — двигает trialEndsAt (пробные дни, в выручку не попадают);
//   • Оплаченный период — ставит ACTIVE, двигает currentPeriodEnd и (опционально)
//     пишет платёж method='cash' в историю и в выручку.
// Даты считает бэкенд той же формулой, что автосписание (см.
// wholesaler/billing.service → applyWholesalerSuccess), поэтому ручное и
// автоматическое продление не расходятся. В отличие от поставщика, здесь нет
// «витрины» — подписка определяет только доступ к самому кабинету.

import { useState } from 'react';
import { Banknote, CalendarPlus, Check, KeyRound, Copy } from 'lucide-react';

import AccessCountdown from '@/components/AccessCountdown';
import {
  activateWholesalerPaid,
  adjustWholesalerTrial,
  resetWholesalerPassword,
  setWholesalerSubscriptionStatus,
  type WholesalerDetail,
  type WholesalerSubStatusUpper,
} from '@/lib/operatorApi';
import { formatDate, formatKop } from '@/lib/format';

const SUB_LABEL: Record<string, string> = {
  trial: 'Триал',
  active: 'Подписка оплачена',
  past_due: 'Просрочена',
  cancelled: 'Отменена',
};

const SUB_OPTIONS: { value: WholesalerSubStatusUpper; label: string }[] = [
  { value: 'TRIAL', label: 'Триал' },
  { value: 'ACTIVE', label: 'Активна' },
  { value: 'PAST_DUE', label: 'Просрочена' },
  { value: 'CANCELLED', label: 'Отменена' },
];

const PAYMENT_STATUS: Record<string, string> = {
  SUCCEEDED: 'Оплачен',
  PENDING: 'Ожидает',
  FAILED: 'Ошибка',
  REFUNDED: 'Возврат',
};

type Mode = 'trial' | 'paid';
type Span = 'days' | 'until';

export default function WholesalerSubscription({
  detail,
  onSaved,
}: {
  detail: WholesalerDetail;
  onSaved: (msg: string) => Promise<void> | void;
}) {
  const [mode, setMode] = useState<Mode>(detail.subscriptionStatus === 'active' ? 'paid' : 'trial');
  const [span, setSpan] = useState<Span>('days');
  const [days, setDays] = useState(30);
  const [until, setUntil] = useState('');
  const [recordPayment, setRecordPayment] = useState(true);
  const [amount, setAmount] = useState(String(Math.round(detail.priceKop / 100)));
  const [note, setNote] = useState('');
  const [approve, setApprove] = useState(detail.status !== 'active');
  const [subStatus, setSubStatus] = useState<WholesalerSubStatusUpper>(
    detail.subscriptionStatus.toUpperCase() as WholesalerSubStatusUpper,
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState<string | null>(null);

  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    setErr(null);
    try {
      const msg = await fn();
      await onSaved(msg);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  const extend = () =>
    run(async () => {
      if (span === 'days' && (!Number.isInteger(days) || days <= 0)) {
        throw new Error('Введите количество дней');
      }
      if (span === 'until' && !until) throw new Error('Выберите дату');
      const untilIso = until ? new Date(`${until}T23:59:59`).toISOString() : undefined;

      if (mode === 'trial') {
        const r = await adjustWholesalerTrial(detail.id, {
          ...(span === 'days' ? { days } : { until: untilIso }),
        });
        return `Триал продлён до ${r.trialEndsAt ? formatDate(r.trialEndsAt) : '—'}`;
      }

      const rub = Number(amount.replace(',', '.'));
      if (recordPayment && (!Number.isFinite(rub) || rub < 0)) {
        throw new Error('Некорректная сумма платежа');
      }
      const r = await activateWholesalerPaid(detail.id, {
        ...(span === 'days' ? { days } : { until: untilIso }),
        recordPayment,
        approve,
        ...(recordPayment ? { amountKopecks: Math.round(rub * 100) } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      return `Подписка активна до ${
        r.currentPeriodEnd ? formatDate(r.currentPeriodEnd) : '—'
      }`;
    });

  const applyStatus = () =>
    run(async () => {
      await setWholesalerSubscriptionStatus(detail.id, subStatus);
      return `Статус подписки: ${SUB_OPTIONS.find((o) => o.value === subStatus)?.label}`;
    });

  const resetPassword = () =>
    run(async () => {
      const r = await resetWholesalerPassword(detail.id);
      setNewPassword(r.password);
      return 'Пароль сброшен — передайте новый Оптовику';
    });

  return (
    <div className="space-y-4">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-ink/40">Подписка</h3>

      {detail.accessEndsAt && (
        <AccessCountdown
          endsAt={detail.accessEndsAt}
          variant="full"
          subject={detail.subscriptionStatus === 'active' ? 'paid' : 'trial'}
          startedAt={detail.currentPeriodStart ?? detail.createdAt}
          accessState={detail.accessState}
        />
      )}

      <dl className="divide-y divide-line border border-line">
        <InfoRow label="Статус подписки">
          {SUB_LABEL[detail.subscriptionStatus] ?? detail.subscriptionStatus}
        </InfoRow>
        <InfoRow label="Триал до">
          {detail.trialEndsAt ? formatDate(detail.trialEndsAt) : '—'}
        </InfoRow>
        <InfoRow label="Оплачено до">
          {detail.currentPeriodEnd ? formatDate(detail.currentPeriodEnd) : '—'}
        </InfoRow>
        <InfoRow label="Автопродление">
          {detail.autoRenew ? 'Включено (карта привязана)' : 'Нет карты / отключено'}
        </InfoRow>
        <InfoRow label="Тариф">{formatKop(detail.priceKop)} / мес</InfoRow>
        <InfoRow label="Заработано всего">{formatKop(detail.lifetimeRevenue)}</InfoRow>
      </dl>

      <div className="border border-line p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-ink/60">Продлить как</span>
          <Segmented
            value={mode}
            onChange={(v) => setMode(v as Mode)}
            options={[
              { value: 'trial', label: 'Триал' },
              { value: 'paid', label: 'Оплаченный период' },
            ]}
          />
        </div>

        <p className="mt-2 text-[12px] leading-relaxed text-ink/55">
          {mode === 'trial'
            ? 'Пробные дни: подписка станет TRIAL (если была просрочена), в выручку ничего не попадёт.'
            : 'Подписка станет ACTIVE. Срок считается от текущей даты окончания, оплаченные и пробные дни не сгорают.'}
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Segmented
            value={span}
            onChange={(v) => setSpan(v as Span)}
            options={[
              { value: 'days', label: 'На дни' },
              { value: 'until', label: 'До даты' },
            ]}
          />
        </div>

        {span === 'days' ? (
          <div className="mt-3">
            <label className="eyebrow">Количество дней</label>
            <input
              className="input mt-1.5"
              type="number"
              min={1}
              max={1100}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            />
            <div className="mt-2 flex flex-wrap gap-1">
              {[7, 14, 30, 90, 180, 365].map((n) => (
                <button
                  key={n}
                  type="button"
                  className="border border-line px-2 py-1 text-[11px] text-ink/70 hover:border-ink hover:text-ink"
                  onClick={() => setDays(n)}
                >
                  +{n}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-3">
            <label className="eyebrow">Дата окончания</label>
            <input
              className="input mt-1.5"
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
        )}

        {mode === 'paid' && (
          <div className="mt-3 space-y-3">
            <label className="flex items-center gap-2 text-[13px] text-ink/80">
              <input
                type="checkbox"
                checked={recordPayment}
                onChange={(e) => setRecordPayment(e.target.checked)}
              />
              Записать платёж наличными в историю и выручку
            </label>

            {recordPayment && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="eyebrow">Сумма, ₽</label>
                  <input
                    className="input mt-1.5"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
                <div>
                  <label className="eyebrow">Комментарий</label>
                  <input
                    className="input mt-1.5"
                    placeholder="Оплата за март, наличные"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
              </div>
            )}

            {detail.status !== 'active' && (
              <label className="flex items-center gap-2 text-[13px] text-ink/80">
                <input
                  type="checkbox"
                  checked={approve}
                  onChange={(e) => setApprove(e.target.checked)}
                />
                Заодно одобрить модерацию (ACTIVE)
              </label>
            )}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button className="btn-primary" type="button" disabled={busy} onClick={extend}>
            {mode === 'trial' ? (
              <CalendarPlus size={14} strokeWidth={1.75} />
            ) : (
              <Banknote size={14} strokeWidth={1.75} />
            )}
            {busy ? 'Сохраняю…' : mode === 'trial' ? 'Продлить триал' : 'Активировать период'}
          </button>
        </div>
      </div>

      <div className="grid gap-3 border border-line p-4 sm:grid-cols-2">
        <div>
          <label className="eyebrow">Статус подписки вручную</label>
          <div className="mt-1.5 flex gap-2">
            <select
              className="input"
              value={subStatus}
              onChange={(e) => setSubStatus(e.target.value as WholesalerSubStatusUpper)}
            >
              {SUB_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button className="btn-outline shrink-0" type="button" disabled={busy} onClick={applyStatus}>
              <Check size={14} strokeWidth={1.75} />
              Применить
            </button>
          </div>
          <p className="mt-1.5 text-[11px] text-ink/50">
            ACTIVE без живого срока не примем — статус откатится часовым биллингом.
          </p>
        </div>

        <div>
          <label className="eyebrow">Доступ в кабинет</label>
          <div className="mt-1.5">
            <button className="btn-outline" type="button" disabled={busy} onClick={resetPassword}>
              <KeyRound size={14} strokeWidth={1.75} />
              Сбросить пароль
            </button>
          </div>
          {newPassword ? (
            <div className="mt-2 flex items-center gap-2 border border-amber-500/40 bg-amber-50 px-3 py-2">
              <code className="font-mono text-[13px] text-ink">{newPassword}</code>
              <button
                type="button"
                className="text-ink/50 hover:text-ink"
                title="Скопировать"
                onClick={() => void navigator.clipboard?.writeText(newPassword)}
              >
                <Copy size={14} strokeWidth={1.75} />
              </button>
            </div>
          ) : (
            <p className="mt-1.5 text-[11px] text-ink/50">
              Новый пароль показывается один раз — сохраните и передайте Оптовику.
            </p>
          )}
        </div>
      </div>

      {err && (
        <div className="border border-red-600/30 bg-red-50 px-3 py-2 text-[12px] text-red-700">
          {err}
        </div>
      )}

      {detail.payments.length > 0 && (
        <div>
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink/40">
            Платежи по подписке
          </h4>
          <ul className="divide-y divide-line border border-line">
            {detail.payments.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 text-[12px]">
                <div>
                  <div className="text-ink/90">{formatKop(p.amount)}</div>
                  <div className="text-[11px] text-ink/50">
                    {formatDate(p.paidAt ?? p.createdAt)}
                    {p.method ? ` · ${p.method === 'cash' ? 'наличные' : p.method}` : ''}
                  </div>
                </div>
                <span
                  className={
                    p.status === 'SUCCEEDED'
                      ? 'text-green-700'
                      : p.status === 'FAILED'
                        ? 'text-red-700'
                        : 'text-ink/60'
                  }
                >
                  {PAYMENT_STATUS[p.status] ?? p.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Segmented({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="inline-flex border border-line">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`px-3 py-1.5 text-[12px] ${
            value === o.value ? 'bg-ink text-white' : 'text-ink/70'
          }`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-3 py-2 text-[13px]">
      <dt className="text-ink/50">{label}</dt>
      <dd className="text-right text-ink/90">{children}</dd>
    </div>
  );
}
